import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./AdminActionLog.module.css";
import api from "../../api/axois";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";

const AdminActionLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, per_page: 15, current_page: 1, last_page: 1 });
  const debounceTimer = useRef(null);

  const fetchLogs = useCallback(async (searchVal, pageVal) => {
    setLoading(true);
    try {
      const res = await api.get("/admin-action-logs", {
        params: { search: searchVal, page: pageVal },
      });
      setLogs(res.data.logs || []);
      setPagination(res.data.pagination || { total: 0, per_page: 15, current_page: 1, last_page: 1 });
    } catch (err) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(search, page);
  }, [search, page, fetchLogs]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const { current_page, last_page } = pagination;
    const pages = [];
    const delta = 2;
    const left = Math.max(1, current_page - delta);
    const right = Math.min(last_page, current_page + delta);
    for (let i = left; i <= right; i++) pages.push(i);
    return pages;
  };

  return (
    <div className={styles.pageWrapper}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.contentWrapper}>
        <Header title="Admin Action Log" onMenuClick={() => setSidebarOpen(true)} />
        <div className={styles.topBar}>
          <h2 className={styles.title}>Admin Actions Log</h2>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="Search by user, action or details..."
              value={searchInput}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
          </div>
        </div>

        {loading ? (
          <div className={styles.stateMsg}>Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className={styles.stateMsg}>No admin actions found.</div>
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.logTable}>
                <thead>
                  <tr>
                    <th>User Name</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>Date/Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.admin_name}</td>
                      <td>{log.action}</td>
                      <td>{log.details}</td>
                      <td>{log.created_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.paginationBar}>
              <span className={styles.paginationInfo}>
                Showing {(pagination.current_page - 1) * pagination.per_page + 1}–
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
              </span>
              <div className={styles.paginationControls}>
                <button
                  className={styles.pageBtn}
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  ‹
                </button>
                {getPageNumbers().map((p) => (
                  <button
                    key={p}
                    className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ""}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className={styles.pageBtn}
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pagination.last_page}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminActionLog;
